import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography,
  Paper,
  List,
  ListItem,
  TextField,
  IconButton,
  CircularProgress,
  Fade,
  Grow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { 
  Chat as ChatIcon, 
  Close as CloseIcon, 
  Send as SendIcon
} from '@mui/icons-material';
import io from 'socket.io-client';
import axios from 'axios';

const API_BASE_URL = 'https://agent.1lb6ey6g0jkt.iconaccounting.ie';

const ChatWidget = React.memo(() => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [hasShownWelcomeMessage, setHasShownWelcomeMessage] = useState(false);

  const [chatRestored, setChatRestored] = useState(false);
  const [showEndChatConfirm, setShowEndChatConfirm] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // No longer checking for existing chats - each session is fresh for privacy
  useEffect(() => {
    // Component is ready for new chat
    console.log('🔒 Chat widget ready - each session will be fresh for privacy');
  }, []);

  const handleOpen = async () => {
    setOpen(true);
    
    if (!sessionId) {
      try {
        setLoading(true);
        
        // Always create a fresh chat session for privacy
        const response = await axios.post(`${API_BASE_URL}/api/chats/create`);
        
        setSessionId(response.data.sessionId);
        
        // Load existing messages (should only be welcome message)
        await loadMessages(response.data.sessionId);
        
      } catch (error) {
        console.error('Failed to start chat:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const showWelcomeMessage = () => {
    const welcomeMessage = {
      id: Date.now(),
      message: "👋 Welcome! I'm here to help you with any questions about Icon Accounting. Feel free to ask anything! If you'd like to provide your name and email for a more personalized experience, you can simply tell me, but it's completely optional.",
      sender_type: 'support',
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, welcomeMessage]);
    setHasShownWelcomeMessage(true);
  };

  const loadMessages = async (sessionId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/chats/messages/${sessionId}`);
      
      // Check if messages are different from current state
      setMessages(prevMessages => {
        const currentMessageIds = prevMessages.map(msg => msg.id).sort();
        const newMessageIds = response.data.map(msg => msg.id).sort();
        
        const isSame = JSON.stringify(currentMessageIds) === JSON.stringify(newMessageIds);
        
        if (isSame) {
          return prevMessages;
        } else {
          return response.data;
        }
      });
      
      if (!hasShownWelcomeMessage && response.data.length === 0) {
        showWelcomeMessage();
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const connectSocket = useCallback(() => {
    if (!sessionId) return;

    const newSocket = io(API_BASE_URL);
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('Connected to chat server');
      newSocket.emit('join_chat', sessionId);
    });

    newSocket.on('new_message', (message) => {
      setMessages(prev => [...prev, message]);
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from chat server');
    });

    return () => {
      newSocket.disconnect();
    };
  }, [sessionId]);

  useEffect(() => {
    if (sessionId && open) {
      // Load messages initially
      loadMessages(sessionId);
      
      // Set up polling for new messages
      const messageInterval = setInterval(() => {
        loadMessages(sessionId);
      }, 1000); // Check for new messages every 1 second for faster response
      
      // const cleanup = connectSocket();
      // return cleanup;
      
      return () => {
        clearInterval(messageInterval);
      };
    }
  }, [sessionId, open]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !sessionId) return;

    const messageData = {
      sessionId,
      message: inputMessage.trim(),
      sender_type: 'user',
      sender_id: 'customer'
    };

    try {
      // Save message via API
      await axios.post(`${API_BASE_URL}/api/chats/message`, messageData);
      
      // Check if message contains name/email info and update user info
      const messageText = inputMessage.trim().toLowerCase();
      console.log('🔍 Checking message for name patterns:', messageText);
      
      // More comprehensive name extraction patterns
      const namePatterns = [
        /(?:my name is|i am|call me|i'm|im)\s+([a-zA-Z\s]+?)(?:\s|$|\.|,|!)/i,
        /(?:this is|it's|its)\s+([a-zA-Z\s]+?)(?:\s|$|\.|,|!)/i,
        /(?:name's|name is)\s+([a-zA-Z\s]+?)(?:\s|$|\.|,|!)/i
      ];
      
      for (const pattern of namePatterns) {
        const nameMatch = messageText.match(pattern);
        if (nameMatch && !userName) {
          const extractedName = nameMatch[1].trim();
          // Clean up the name (remove extra words, capitalize properly)
          const cleanName = extractedName
            .replace(/\b(and|the|a|an|is|am|are|was|were|be|been|being|have|has|had|do|does|did|will|would|could|should|may|might|can|must|shall)\b/gi, '')
            .trim()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
            .substring(0, 50); // Limit name length
          
          if (cleanName && cleanName.length > 1) {
            console.log('✅ Extracted name:', cleanName);
            setUserName(cleanName);
            await updateUserInfo(cleanName, userEmail);
            break; // Stop after first successful match
          }
        }
      }
      
      if (messageText.includes('@') && !userEmail) {
        // Extract email from message
        const emailMatch = messageText.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
        if (emailMatch) {
          const extractedEmail = emailMatch[1];
          setUserEmail(extractedEmail);
          await updateUserInfo(userName, extractedEmail);
        }
      }
      
      setInputMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const updateUserInfo = async (name, email) => {
    if (!sessionId) {
      console.log('❌ No session ID for user info update');
      return;
    }
    
    try {
      const updateData = {
        name: name || userName,
        email: email || userEmail
      };
      
      console.log('📤 Updating user info:', updateData);
      
      const response = await axios.put(`${API_BASE_URL}/api/chats/update-user/${sessionId}`, updateData);
      console.log('✅ User info updated successfully:', response.data);
    } catch (error) {
      console.error('❌ Failed to update user info:', error.response?.data || error.message);
    }
  };

  const startNewChat = async () => {
    try {
      setLoading(true);
      setChatRestored(false);
      setMessages([]);
      setHasShownWelcomeMessage(false);
      
      const response = await axios.post(`${API_BASE_URL}/api/chats/create`);
      
      setSessionId(response.data.sessionId);
      await loadMessages(response.data.sessionId);
      
    } catch (error) {
      console.error('Failed to start new chat:', error);
    } finally {
      setLoading(false);
    }
  };

  const finishChat = () => {
    setShowEndChatConfirm(true);
  };

  const confirmFinishChat = async () => {
    if (!sessionId) return;

    try {
      setLoading(true);
      setShowEndChatConfirm(false);
      
      // End the chat session
      await axios.put(`${API_BASE_URL}/api/chats/end/${sessionId}`, {
        rating: 5 // Default rating, could be made configurable
      });

      console.log('✅ Chat finished by customer');
      
      // Add a system message indicating the chat was ended
      const endMessage = {
        id: Date.now(),
        message: "👋 Thank you for chatting with us! This conversation has ended. Feel free to start a new chat if you need further assistance.",
        sender_type: 'system',
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, endMessage]);
      
      // Clear the session after a delay to show the end message
      setTimeout(() => {
        setSessionId(null);
        setMessages([]);
        setChatRestored(false);
        setHasShownWelcomeMessage(false);
        setOpen(false);
      }, 3000);
      
    } catch (error) {
      console.error('❌ Failed to finish chat:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      {/* Chat Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000
        }}
      >
        <Fade in={!open}>
          <IconButton
            onClick={handleOpen}
            sx={{
              width: 65,
              height: 65,
              background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
              color: 'white',
              borderRadius: '50%',
              boxShadow: '0 8px 32px rgba(0, 170, 229, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0099cc 0%, #0088b3 100%)',
                transform: 'scale(1.1) translateY(-2px)',
                boxShadow: '0 12px 40px rgba(0, 170, 229, 0.6)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <ChatIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Fade>
      </Box>

      {/* Chat Window */}
      <Grow in={open}>
        <Paper
          sx={{
            position: 'fixed',
            bottom: 100,
            right: 24,
            width: 400,
            height: 600,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
              color: 'white',
              p: 2.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              ICON Accounting Support
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
                              {sessionId && (
                  <IconButton
                    color="inherit"
                    onClick={finishChat}
                    size="small"
                    title="End Chat"
                    sx={{ 
                      fontSize: '0.75rem',
                      borderRadius: '12px',
                      px: 1.5,
                      py: 0.5,
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.2)'
                      }
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      End Chat
                    </Typography>
                  </IconButton>
                )}
                              {chatRestored && (
                  <IconButton
                    color="inherit"
                    onClick={startNewChat}
                    size="small"
                    title="Start New Chat"
                    sx={{ 
                      fontSize: '0.75rem',
                      borderRadius: '12px',
                      px: 1.5,
                      py: 0.5,
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.2)'
                      }
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      New Chat
                    </Typography>
                  </IconButton>
                )}
              <IconButton
                color="inherit"
                onClick={() => setOpen(false)}
                size="small"
                sx={{
                  borderRadius: '50%',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Restored Chat Banner */}
          {chatRestored && (
            <Box
              sx={{
                background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
                color: 'white',
                p: 1.5,
                textAlign: 'center',
                fontSize: '0.875rem',
                fontWeight: 500,
                borderRadius: '0 0 20px 20px',
                boxShadow: '0 2px 8px rgba(0, 170, 229, 0.3)'
              }}
            >
              🔄 Chat session restored
            </Box>
          )}

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2.5,
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                    <CircularProgress sx={{ color: '#00aae5' }} />
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {messages.map((message, index) => (
                  <ListItem
                    key={message.id || index}
                    sx={{
                      display: 'block',
                      p: 0,
                      mb: 1
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: message.sender_type === 'user' ? 'flex-end' : 'flex-start'
                      }}
                    >
                      <Paper
                        sx={{
                          p: 2,
                          maxWidth: '80%',
                          background: message.sender_type === 'user' 
                            ? 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)'
                            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                          color: message.sender_type === 'user' ? 'white' : '#2d3748',
                          borderRadius: '18px',
                                                      boxShadow: message.sender_type === 'user'
                              ? '0 4px 12px rgba(0, 170, 229, 0.3)'
                              : '0 2px 8px rgba(0, 0, 0, 0.1)',
                          border: message.sender_type === 'user' 
                            ? 'none'
                            : '1px solid rgba(0, 0, 0, 0.05)'
                        }}
                      >
                        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                          {message.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 1,
                            opacity: message.sender_type === 'user' ? 0.8 : 0.6,
                            fontSize: '0.75rem'
                          }}
                        >
                          {formatTime(message.timestamp)}
                        </Typography>
                      </Paper>
                    </Box>
                  </ListItem>
                ))}
                <div ref={messagesEndRef} />
              </List>
            )}
          </Box>

          {/* Input */}
          <Box sx={{ 
            p: 2.5, 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderTop: '1px solid rgba(0, 0, 0, 0.05)',
            borderBottomLeftRadius: '20px',
            borderBottomRightRadius: '20px'
          }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Type your message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '25px',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
                    }
                  }
                }}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || loading}
                sx={{
                  background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
                  color: 'white',
                  borderRadius: '50%',
                  width: 45,
                  height: 45,
                  boxShadow: '0 4px 12px rgba(0, 170, 229, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0099cc 0%, #0088b3 100%)',
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 16px rgba(0, 170, 229, 0.4)'
                  },
                  '&:disabled': {
                    background: '#e2e8f0',
                    color: '#a0aec0',
                    boxShadow: 'none'
                  }
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Grow>

      {/* End Chat Confirmation Dialog */}
      <Dialog
        open={showEndChatConfirm}
        onClose={() => setShowEndChatConfirm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0',
          fontWeight: 600
        }}>
          End Chat Session
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
            Are you sure you want to end this chat session? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button 
            onClick={() => setShowEndChatConfirm(false)}
            disabled={loading}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmFinishChat}
            disabled={loading}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1,
              background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
              color: 'white',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                background: 'linear-gradient(135deg, #c53030 0%, #a02323 100%)'
              }
            }}
          >
            End Chat
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default ChatWidget;
